import { Injectable } from "@angular/core";
import { SampleSheetTemplate } from "@bioconnect-ui/biosample-service";
import { ValidationReport } from "@bioconnect-ui/file-type-service";
import { QuantityKind, Unit } from "@bioconnect-ui/measure-service";
import { Protocol, ProtocolParameter } from "@bioconnect-ui/protocol-service";
import { Resource } from "@bioconnect-ui/resource-service";
import { Comment } from "@bioconnect-ui/shared-service";
import { Origin } from "@bioconnect-ui/admin-service";
import { ContextData } from "@bioconnect-ui/file-service";

export interface CurationServiceConfig {
  restServer: string;
}

// The following types are defined in the isa model here:
// https://isa-specs.readthedocs.io/en/latest/isajson.html#data-schema-json
// They will be used to qualify files
export enum FileCategory {
  RAW = "Raw Data File",
  DERIVED = "Derived Data File",
  IMAGE = "Image File",
}

export interface Assay {
    id?: number;
    identifier: string;
    description: string;
    investigation?: number;
    investigation_identifier?: string;
    study?: number;
    study_identifier?: string;
    measurement_type?: OntologyAnnotation;
    measurement_type_id?: number;
    technology_platform?: string;
    technology_type?: OntologyAnnotation;
    technology_type_id?: number;
    date_completed?: string;
    data?: DataFile[];
    characteristic_categories?: MaterialAttribute[];
    characteristic_values?: OntologyAnnotation[];
    characteristic_value_ids?: number[];
    process_sequence?: Process[];
    protocols?: Protocol[];
    samples?: number[];
    sample_ids?: number[];
    sample_sheet_template?: SampleSheetTemplate;
    sample_sheet_template_id?: number | null;
    other_materials?: Material[];
    unit_categories?: OntologyAnnotation[];
    comments?: Comment[];
    origin_of_samples?: number | null;
    sample_count?: number;
}

export interface Center {
    id?: number;
    name: string;
    abbreviation: string;
    url: string;
    contact?: number;
}

export interface Contact {
    id?: number;
    last_name: string;
    mid_initial?: string;
    first_name: string;
    full_name?: string;
    address?: string
    email: string;
    phone?: string;
    affiliation?: string;
    fax?: string;
    comments: Comment[];
}

export interface StudyReportFactor {
  factor_name: string;
  characteristic: MaterialAttribute;
  values: StudyFactorValue[];
  unit: Unit;
}

export interface StudyFactorValue {
  value_text: string;
  value: OntologyAnnotation;
  unit: OntologyAnnotation
}

export interface StudyFactor {
  id?: number;
  study_id?: number;
  factor: MaterialAttribute;
  values?: MaterialAttributeValue[];
  unit?: Unit;
}

export interface DataFile {
    id?: number;
    name: string;
    filename?: string;
    category?: FileCategory;
    file_id?: number;
    // same as the file_id
    file?: number;
    sds_filetype_id?: number;
    // filetype name
    file_type?: string;
    assays?: Assay[];
    assay_ids?: number[];
    created: string;
    size?: number;
    url?: string;
    measurement_types?: OntologyAnnotation[];
    validation_report?: ValidationReport;
}

export interface Investigation {
    id?: number;
    filename?: string;
    identifier: string;
    title: string;
    description: string;
    submission_date?: string;
    public_release_date?: string;
    contacts: InvestigationContact[];
    contact_ids?: number[];
    publications: Publication[];
    publication_ids?: number[];
    comments: Comment[];
    study_set?: Study[];
}

export interface InvestigationContact {
  id?: number;
  investigation?: Investigation;
  contact: Contact;
  investigation_id: number;
  contact_id: number;
  order: number;
  roles: OntologyAnnotation[];
  role_ids?: number[];
}

export interface Material {
    id?: number | null;
    name: string;
    type: string;
    // todo: add characteristics and derivesfrom?
}

export interface MaterialAttribute {
    id?: number;
    characteristic_type: OntologyAnnotation;
    is_source_characteristic?: boolean;
    quantity_kind?: QuantityKind;
    quantity_kind_id?: number | null;
    units?: Unit[];
}

export interface MaterialAttributeValue {
    id?: number;
    category: MaterialAttribute;
    value?: OntologyAnnotation;
    value_text: string;
    unit?: OntologyAnnotation;
}

export interface OntologySourceReference {
    id: number;
    description: string;
    file: string;
    name: string;
    version: string;
}

export interface OntologyAnnotation {
    id?: number;
    annotation_value: string;
    term_source?: OntologySourceReference;
    term_source_id?: number;
    term_accession: string;
    dependent_annotations?: OntologyAnnotation[]
    term_accession_url: string;
}

export interface Process {
    id?: number;
    name: string;
    execute_protocol?: Protocol;
    execute_protocol_id?: number;
    previous_process_id?: number;
    next_process_id?: number;
    performer: string;
    date: string;
    input_type: InputType;
    output_type: OutputType;
    process_inputs: ProcessInput[];
    process_outputs: ProcessOutput[];
    input_collection: ProcessInputCollection;
    output_collection: ProcessOutputCollection;
    parameter_values: ProcessParameterValue[];
    resources?: Resource[];
}

export interface ProcessParameterValue {
  id?: number;
  process?: Process;
  category?: ProtocolParameter;
  value?: OntologyAnnotation[];
  value_text: string;
}

export interface ProcessInput {
    id: number;
    process: number;
    source?: string;
    sample?: string;
    material?: string;
    data?: string;
}

export interface ProcessOutput {
    id: number;
    process: number;
    sample?: string;
    material?: string;
    data?: string;
}
export interface ProcessInputCollection {
  sources: number[];
  samples: number[];
  materials: number[];
  data: number[];
}
export interface ProcessOutputCollection {
  samples: number[];
  materials: number[];
  data: number[];
}

export interface Publication {
    id?: number;
    pub_med_id?: string;
    doi?: string;
    title: string;
    author_list?: string;
    status?: OntologyAnnotation;
    status_id?: number;
    investigation_count?: number;
    study_count?: number;
}

export interface Sample {
    id: number;
    unique_id: string;
    origin?: Origin;
    origin_id?: number;
    name: string;
    derives_from: Source[];
    source: string;
    characteristics: MaterialAttributeValue[];
    comments: Comment[];
}

export interface SampleCharacteristics {
  id: number;
  source_id: string;
  characterisic_id: number;
  characterisic: MaterialAttributeValue;
}

export interface SearchTerm {
  field: string,
  term: string
}

export interface Source {
    id: number;
    unique_id: string;
    origin?: Origin;
    origin_id?: number;
    name: string;
    characteristics: MaterialAttributeValue[];
    comments: Comment[];
}

export interface Study {
    id?: number;
    identifier?: string;
    description?: string;
    title?: string;
    investigation?: number;
    investigation_identifier?: string;
    assay_set?: Assay[];    
    center?: Center;
    center_id?: number;
    characteristic_categories?: MaterialAttribute[];
    characteristic_values?: OntologyAnnotation[];
    characteristic_value_ids?: number[];
    comments?: Comment[];
    contacts?: StudyContact[];
    contact_ids?: number[];
    design_descriptors?: OntologyAnnotation[];
    design_descriptor_ids?: number[];
    protocols?: Protocol[];
    protocol_ids?: number[];
    publications?: Publication[];
    publication_ids?: number[];
    sources?: Source[];
    source_ids?: number[];
    status?: OntologyAnnotation;
    status_id?: number;
    study_factors?: StudyFactor[];
    file_count?: number[];
    assay_count?: number[];
    publication_count?: number[];
    investigators?: string;
    origin_of_sources?: number;
    last_updated?: string;
    sample_sheets?: SampleSheetTemplate[];
    context_data?: ContextData[];
    // todo: add other_materials here?
}

export interface StudyContact {
  id?: number;
  study?: Study;
  contact: Contact;
  study_id: number;
  contact_id: number;
  order: number;
  roles: OntologyAnnotation[];
  role_ids?: number[];
}

export enum InputType {
  SOURCE = 'Source',
  SAMPLE = 'Sample',
  MATERIAL = 'Material',
  DATA = 'Data',
  UNKNOWN = 'Unknown'
}

export enum OutputType {
  SAMPLE = 'Sample',
  MATERIAL = 'Material',
  DATA = 'Data',
  UNKNOWN = 'Unknown'
}


@Injectable({
    providedIn: 'root',
})

export class CurationServiceTypes {

}
